import { IPromise } from 'angular';
import { Formatters, Messages } from '../../codegen/Localize';
import {
  ActionHelpers, standardActionErrorMsg,
  standardActionSuccessMsg, StandardActionToastMessages
} from '../../commonInfra/contentActions/ActionHelpers';
import {
  IErrorDetail
} from '../../commonInfra/contentActions/ActionMessages';
import { IConfirmDialogTagsScope } from '../../commonInfra/contentActions/ContentActions';
import { DataSourceErrors } from '../../content/services/DataSourceErrors';
import { ViewErrors } from '../../content/services/ViewErrors';
import { IErrorItem } from '../../commonInfra/contentActions/MultipleItemResult';
import { FlowErrors } from '../../flow/services/FlowErrors';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { Metrics } from '../../metrics/resources/Metrics';
import { MetricErrors } from '../../metrics/services/MetricErrors';
import { PublishedConnections } from '../../publishedConnection/resources/PublishedConnections';
import { Views } from '../../views/resources/Views';
import { ITagWithCount } from '../directives/tbTagsInput';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode, TagErrorCode } from '../helpers/ErrorCodes';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { TagsHelper } from '../helpers/TagsInputHelper';
import { IContentAndLuidInfo } from '../mappers/DatabaseAndTableMapper';
import { FlowListItemInfo } from '../models/FlowInfo';
import { IContentInfo } from '../models/IContentInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { IPublishedConnectionInfo } from '../models/IPublishedConnectionInfo';
import { IViewInfo } from '../models/IViewInfo';
import { IWorkbookBase } from '../models/IWorkbookInfo';
import { INamedItemInfo, ITaggedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Databases } from '../../catalog/resources/Databases';
import { DataSources } from '../../content/resources/Datasources';
import { Flows } from '../resources/Flows';
import { Tables } from '../../catalog/resources/Tables';
import { Workbooks } from '../../content/resources/Workbooks';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { PublishedConnectionErrors } from './PublishedConnectionErrors';
import { RestApiErrorResponse } from './RestApiService';
import { ToasterService, ToastType } from './ToasterService';
import { WorkbookErrors } from './WorkbookErrors';


export interface INamedAndTaggedItemInfo extends INamedItemInfo, ITaggedItemInfo { }

export interface IChangeTagsExecuteFnParams<T> {
  changeTagsAction: (ids: string[], addTags: string[], removeTags: string[]) => IPromise<IResult>;
  getDescription: (itemSet: Set<T>) => string;
  notificationFns: StandardActionToastMessages;
  errorDetailFn: (errorItem: IErrorItem<T>) => IErrorDetail;
}

export interface IChangeTagsExecuteFnForMixedContentParams<T> {
  getDescription: (itemSet: T[]) => string;
}

export class ChangeTagsActionHelperService {

  public static $inject = [
    '$q',
    'ConfirmActionDialog',
    'WorkbookErrors',
  ];

  constructor(
    private $q: ng.IQService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private WorkbookErrors: WorkbookErrors,
  ) { }

  public createExecuteFn<T extends INamedAndTaggedItemInfo>(params: IChangeTagsExecuteFnParams<T>) {
    return (itemSet: Set<T>, context: IActionContext) => {

      const scope = this.configureScope(context.scope, itemSet, params.getDescription);
      const previousTagSet = TagsHelper.tagSetFromTagsList(scope.tags.model);

      const dialogConfig = {
        scope: scope,
        bodyTemplateUrl: 'confirmActionDialogTags.html',
        title: Messages.changeTags(),
        actionName: Messages.changeTagsConfirm(),
        action: () => {
          const editTags = TagsHelper.getTagsChange(previousTagSet, scope.tags.model);
          return params.changeTagsAction(itemSet.keys(), editTags.addTags, editTags.removeTags).then(result => {
            return {
              ...result,
              tags: scope.tags.model.map(m => TagsHelper.unquote(m.name))
            };
          });
        },
        position: { reserveHeight: 450 }
      };

      const dialogInstance = this.ConfirmActionDialog.open(dialogConfig);

      dialogInstance.result
        .then(result =>
          ActionHelpers.displayStandardToastsForResult(
            itemSet,
            result,
            params.notificationFns,
            params.errorDetailFn
          )
        ).finally(() => scope.$destroy());

      return dialogInstance.result;
    };
  }

  public createExecuteFnForMixedContent(params: IChangeTagsExecuteFnForMixedContentParams<IContentInfo>) {
    return (items: IContentInfo[], context: IActionContext) => {
      const scope = this.configureScopeForMixedContent(context.scope, items, params.getDescription);
      const previousTagSet = TagsHelper.tagSetFromTagsList(scope.tags.model);

      const dialogConfig = {
        scope: scope,
        bodyTemplateUrl: 'confirmActionDialogTags.html',
        title: Messages.changeTags(),
        actionName: Messages.changeTagsConfirm(),
        action: () => this.changeTagsForContent(items, previousTagSet, scope),
        position: { reserveHeight: 450 }
      };

      const dialogInstance = this.ConfirmActionDialog.open(dialogConfig);

      dialogInstance.result
        .then((results: IResult[]) => ({
          ...ResultHelper.combineResults(results),
          tags: scope.tags.model.map(m => TagsHelper.unquote(m.name))
        }))
        .finally(() => scope.$destroy());

      return dialogInstance.result;
    };
  }

  private configureScope<T extends INamedAndTaggedItemInfo>(parentScope: ng.IScope, itemSet: Set<T>, getDescription: (itemSet: Set<T>) => string): IConfirmDialogTagsScope {
    const scope = <IConfirmDialogTagsScope>parentScope.$new();

    scope.description = getDescription(itemSet);

    scope.tags = {
      model: TagsHelper.tagsListFromTaggedItemSet(itemSet)
    };

    return scope;
  }

  private configureScopeForMixedContent<T>(parentScope: ng.IScope, items: T[], getDescription: (itemSet: T[]) => string): IConfirmDialogTagsScope {
    const scope = <IConfirmDialogTagsScope>parentScope.$new();

    scope.description = getDescription(items);

    scope.tags = {
      model: TagsHelper.tagsListFromTaggedItemArray(items)
    };

    return scope;
  }

  private changeTagsForContent(items: IContentInfo[], previousTagSet: Set<ITagWithCount>, scope: IConfirmDialogTagsScope): IPromise<IResult[]> {
    // We can only change the items to a set when we split by content type because different items of different content
    // types could have the same id. A set keyed on id would remove the 'duplicate' id, even though it corresponds to
    // a different item. Ids _are_ unique to content types so making them sets here is safe.
    const views = Set.fromIdItemArray(items.filter(i => i.contentType === 'view'));
    const workbooks = Set.fromIdItemArray(items.filter(i => i.contentType === 'workbook'));
    const datasources = Set.fromIdItemArray(items.filter(i => i.contentType === 'datasource'));
    const flows = Set.fromIdItemArray(items.filter(i => i.contentType === 'flow'));
    const metrics =  Set.fromIdItemArray(items.filter(i => i.contentType === 'metric'));
    const publishedConnections =  Set.fromIdItemArray(items.filter(i => i.contentType === 'publishedConnection'));
    const databases = Set.fromIdItemArray(items.filter(i => i.contentType === 'database'));
    const tables = Set.fromIdItemArray(items.filter(i => i.contentType === 'table'));

    // NOTE: If we ever allow catalog content to be part of mixed content selection,
    // we'll need to filter by content type then ensure luid is included on the filtered collection

    const editTags = TagsHelper.getTagsChange(previousTagSet, scope.tags.model);

    // Note (abrandon 2020-05-05) It appears that no code is currently using allResults. Can we remove it?
    const allResults: IResult[] = [];

    // We can't call these asynchronously because when adding a new tag, the backend creates an id for it. If
    // the calls are done at the same time, the server throws an error (NonUniqueResultException) about the
    // same tag getting two ids. Instead, we have to chain these calls and save the results from each one in an array.
    return this.changeTagsForWorkbooks(workbooks, editTags.addTags, editTags.removeTags)
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForFlows(flows, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForViews(views, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForDatasources(datasources, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForMetrics(metrics, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForPublishedConnections(publishedConnections, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForDatabases(databases, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForTables(tables, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return allResults;
      });
  }

  private changeTagsForViews(views: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (views.size() < 1) {
      return this.$q.when(null);
    }

    return Views.changeTags(views.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        views,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsViewsSuccess),
          error: standardActionErrorMsg(Messages.changeTagsViewsError),
        },
        (errorItem: IErrorItem<IViewInfo>) => ViewErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForFlows(flows: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (flows.size() < 1) {
      return this.$q.when(null);
    }

    return Flows.changeTags(flows.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        flows,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsFlowsSuccess),
          error: standardActionErrorMsg(Messages.changeTagsFlowsError),
        },
        (errorItem: IErrorItem<FlowListItemInfo>) => FlowErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForWorkbooks(workbooks: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (workbooks.size() < 1) {
      return this.$q.when(null);
    }

    return Workbooks.changeTags(workbooks.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        workbooks,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsWorkbooksSuccess),
          error: standardActionErrorMsg(Messages.changeTagsWorkbooksError),
        },
        (errorItem: IErrorItem<IWorkbookBase>) => this.WorkbookErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForDatasources(datasources: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (datasources.size() < 1) {
      return this.$q.when(null);
    }

    return DataSources.changeTags(datasources.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        datasources,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsDatasourcesSuccess),
          error: standardActionErrorMsg(Messages.changeTagsDatasourcesError),
        },
        (errorItem: IErrorItem<IDataSourceBase>) => DataSourceErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForMetrics(metrics: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (metrics.size() < 1) {
      return this.$q.when(null);
    }

    return Metrics.changeTags(metrics.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        metrics,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsMetricsSuccess),
          error: standardActionErrorMsg(Messages.changeTagsMetricsError),
        },
        (errorItem: IErrorItem<IMetricInfo>) => MetricErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForPublishedConnections(publishedConnections: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (publishedConnections.size() < 1) {
      return this.$q.when(null);
    }

    return PublishedConnections.changeTags(publishedConnections.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        publishedConnections,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsPublishedConnectionsSuccess),
          error: standardActionErrorMsg(Messages.changeTagsPublishedConnectionsError),
        },
        (errorItem: IErrorItem<IPublishedConnectionInfo>) => PublishedConnectionErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForDatabases(databases: Set<IContentAndLuidInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    const databasesArray = databases.toArray();
    if (databasesArray.length < 1) {
      return Promise.resolve(null);
    }

    // The REST APIs for external content give a single error message for the whole action,
    // rather than individual messages per content item.
    return Databases.changeTags({ databases: databasesArray, tagsToAdd: addTags, tagsToRemove: removeTags })
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Info,
          text: Messages.changeTagsDatabasesSuccess({
            ITEM_COUNT: databasesArray.length,
            ITEM_COUNT_LOC: Formatters.formatCount(databasesArray.length),
          })
        });
        return {};
      })
      .catch(e => {
        const errorResponse = e as RestApiErrorResponse;
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.changeTagsDatabasesError({
            ITEM_COUNT: databasesArray.length,
            ERROR_MESSAGE: errorResponse.data.error.detail,
          })
        });
        return {};
      });
  }

  private changeTagsForTables(tables: Set<IContentAndLuidInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    const tablesArray = tables.toArray();
    if (tablesArray.length < 1) {
      return Promise.resolve(null);
    }

    // The REST APIs for external content give a single error message for the whole action,
    // rather than individual messages per content item.
    return Tables.changeTags({ tables: tablesArray, tagsToAdd: addTags, tagsToRemove: removeTags })
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Info,
          text: Messages.changeTagsTablesSuccess({
            ITEM_COUNT: tablesArray.length,
            ITEM_COUNT_LOC: Formatters.formatCount(tablesArray.length),
          })
        });
        return {};
      })
      .catch(e => {
        const errorResponse = e as RestApiErrorResponse;
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.changeTagsTablesError({
            ITEM_COUNT: tablesArray.length,
            ERROR_MESSAGE: errorResponse.data.error.detail,
          })
        });
        return {};
      });
  }

  public errorMessageFor(errorItem: IErrorItem<any>): string {
    switch (errorItem.error.code) {
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.changeTagsErrorNoPermission();
      case TagErrorCode.TagNameTooLong:
        return Messages.changeTagsErrorNameTooLong();
      default:
        return Messages.errorUnknown();
    }
  }

}

Module.instance().service('ChangeTagsActionHelperService', ChangeTagsActionHelperService);
